import {FC, useState, useEffect} from 'react'
import {useIntl} from 'react-intl'
import {Link} from 'react-router-dom'
import {LazyLoadImage} from 'react-lazy-load-image-component'
import {OrderItemModel} from '../models/OrderItemModel'
import {ERROR_ISSUES_DETECTED, ITEM_ISSUES_DETECTED, ItemStatus} from '../constants'
import {itemStatusClassMapping} from '../utils'
import ItemActionBox from './ItemActionBox'
import {KTSVG} from '../../../../_metronic/helpers'
import {useAppSelector} from '../../../../setup/redux/hooks'
import {selectSelectedItems, selectSelectedMode, setSelectedItem} from '../redux/OrderRedux'
import {useDispatch} from 'react-redux'
import Lightbox from 'react-image-lightbox'
import 'react-image-lightbox/style.css'

interface Props {
  item: OrderItemModel
}

const PhotoCard: FC<Props> = ({item}) => {
  const intl = useIntl()
  const dispatch = useDispatch()
  const selectMode = useAppSelector(selectSelectedMode)
  const selectedItems = useAppSelector(selectSelectedItems)
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [lightboxIndex, setLightboxIndex] = useState(0)

  const isProcessing = [
    ItemStatus.AI_PROCESSING,
    ItemStatus.UPLOADED,
    ItemStatus.ANALYZING,
  ].includes(item.status)
  const isDone = item.status === ItemStatus.DONE
  const isFailed = item.status === ItemStatus.FAIL

  const detectedIssues = item.note
    ? item.note
        .split(',')
        .map((t) => t.trim())
        .filter((t) => ITEM_ISSUES_DETECTED.includes(t))
    : []

  const inputSrc = item.inputImageUrl?.tiny
  const inputReady = !!inputSrc
  const hasOutput = !!item.outputImageUrl?.tiny
  const isGeneratingThumbnail = isDone && !hasOutput

  const outputSrc =
    isDone || item.status === ItemStatus.PENDING
      ? item.outputImageUrl?.tiny
      : isFailed
      ? item.outputImageUrl?.tiny ?? item.inputImageUrl?.tiny
      : undefined

  const showBlurredLoader = isProcessing || isGeneratingThumbnail || !inputReady
  const [outputLoaded, setOutputLoaded] = useState(false)

  useEffect(() => {
    setOutputLoaded(false)
  }, [outputSrc])

  const lightboxImages = [
    item.inputImageUrl?.medium ?? item.inputImageUrl?.tiny,
    item.outputImageUrl?.medium ?? item.outputImageUrl?.tiny,
  ].filter(Boolean) as string[]

  return (
    <>
      <div className={`photo-card ${showBlurredLoader ? 'photo-card--processing' : ''}`}>
        {/* Selection checkbox */}
        {selectMode && (
          <label className='photo-card__checkbox'>
            <input
              type='checkbox'
              checked={selectedItems.includes(item.id)}
              disabled={item.status !== ItemStatus.DONE}
              onChange={() =>
                dispatch(setSelectedItem({id: item.id, isAdd: !selectedItems.includes(item.id)}))
              }
            />
          </label>
        )}

        {/* Status badge */}
        <span className={`photo-card__status badge ${itemStatusClassMapping(item.status)}`}>
          {intl.formatMessage({id: 'Item.status.' + item.status})}
        </span>

        {/* Before / After images */}
        <div className='photo-card__images'>
          <div
            className='photo-card__img-wrap photo-card__img-wrap--before'
            onClick={() => {
              if (item.inputImageUrl) {
                setLightboxIndex(0)
                setLightboxOpen(true)
              }
            }}
          >
            <span className='photo-card__label'>
              {intl.formatMessage({id: 'OrderDetail.Form.ITEMS'})}
            </span>
            {inputReady ? (
              <LazyLoadImage
                effect='blur'
                src={inputSrc}
                onError={(event: any) => {
                  if (event?.target?.src)
                    event.target.src =
                      inputSrc ?? '' /* fallback to input image if output image fails to load */
                }}
              />
            ) : (
              <div className='photo-card__loader'>
                <div className='photo-card__loader-spinner'>
                  <div className='spinner-border text-primary' role='status' />
                </div>
              </div>
            )}
          </div>

          <div className='photo-card__divider'>
            <KTSVG
              path='/media/icons/duotune/arrows/arr024.svg'
              className='rotate-180 svg-icon-success svg-icon-2hx'
            />
          </div>

          <div
            className='photo-card__img-wrap photo-card__img-wrap--after'
            onClick={() => {
              if (item.outputImageUrl) {
                setLightboxIndex(1)
                setLightboxOpen(true)
              }
            }}
          >
            <span className='photo-card__label'>
              {intl.formatMessage({id: 'OrderDetail.Form.ITEMS_OUTPUT'})}
            </span>
            {showBlurredLoader ? (
              <div className='photo-card__loader'>
                {inputSrc && <img src={inputSrc} alt='' className='photo-card__loader-bg' />}
                <div className='photo-card__loader-spinner'>
                  <div className='spinner-border text-white' role='status' />
                  {isGeneratingThumbnail && (
                    <span className='photo-card__loader-text'>
                      {intl.formatMessage({id: 'ITEM.GENERATING_THUMBNAIL'})}
                    </span>
                  )}
                </div>
              </div>
            ) : (
              <div className='photo-card__output-wrap'>
                {!outputLoaded && inputSrc && (
                  <div className='photo-card__loader'>
                    <img src={inputSrc} alt='' className='photo-card__loader-bg' />
                    <div className='photo-card__loader-spinner'>
                      <div className='spinner-border text-white' role='status' />
                      <span className='photo-card__loader-text'>
                        {intl.formatMessage({id: 'ITEM.GENERATING_THUMBNAIL'})}
                      </span>
                    </div>
                  </div>
                )}
                <LazyLoadImage
                  effect='blur'
                  src={outputSrc}
                  afterLoad={() => setOutputLoaded(true)}
                  onError={(event: any) => {
                    if (event?.target?.src)
                      event.target.src =
                        inputSrc ?? '' /* fallback to input image if output image fails to load */
                  }}
                />
              </div>
            )}
          </div>
        </div>

        {/* Footer: name + issues + actions */}
        <div className='photo-card__footer'>
          <div className='photo-card__footer-left'>
            <Link
              to={`/orders/${item.orderId}/items/${item.id}`}
              className='photo-card__name text-dark fw-bold text-hover-primary'
            >
              {item.name}
            </Link>
            {detectedIssues.length > 0 && (
              <div className='photo-card__issues'>
                {detectedIssues.map((note, i) => {
                  const isError = ERROR_ISSUES_DETECTED.includes(note)
                  return (
                    <span
                      key={i}
                      className={`badge ${isError ? 'badge-danger' : 'badge-warning'} fw-bold`}
                      style={{fontSize: '0.6rem'}}
                    >
                      {intl.formatMessage({id: `ITEM.ISSUE.${note}`})}
                    </span>
                  )
                })}
              </div>
            )}
          </div>
          <ItemActionBox item={item} />
        </div>

        {/* Processing overlay */}
        {isProcessing && <div className='photo-card__overlay' />}
      </div>

      {/* Lightbox */}
      {lightboxOpen && lightboxImages.length > 0 && (
        <Lightbox
          mainSrc={lightboxImages[lightboxIndex] || lightboxImages[0]}
          nextSrc={
            lightboxImages.length > 1
              ? lightboxImages[(lightboxIndex + 1) % lightboxImages.length]
              : undefined
          }
          prevSrc={
            lightboxImages.length > 1
              ? lightboxImages[(lightboxIndex - 1 + lightboxImages.length) % lightboxImages.length]
              : undefined
          }
          onMoveNextRequest={() => setLightboxIndex((lightboxIndex + 1) % lightboxImages.length)}
          onMovePrevRequest={() =>
            setLightboxIndex((lightboxIndex - 1 + lightboxImages.length) % lightboxImages.length)
          }
          onCloseRequest={() => setLightboxOpen(false)}
        />
      )}
    </>
  )
}

export default PhotoCard
